'use client';

import { useEffect } from 'react';
import { createMicroservicesClient } from '@cecm-microservices-sdk/core/client';
import type { IClientSDK } from '@sxm-web/client-sdk-core';
import { getRuntimeConfig } from '@/services/runtimeConfig';

export default function WebSdkBootstrap(): null {
    useEffect(() => {
        const load = async () => {
            try {
                const browserWindow = window as BrowserWindow;
                const runtimeConfig = getRuntimeConfig();

                if (browserWindow.clientSdk || browserWindow.__clientSdkBootstrapPromise) {
                    browserWindow.__clientSdkBootstrapPromise =
                        browserWindow.__clientSdkBootstrapPromise ?? Promise.resolve(browserWindow.clientSdk);
                    return;
                }

                if (!runtimeConfig.clientSdk.edge.baseApiUrl || !runtimeConfig.microservices.baseUrlUS) {
                    console.warn(
                        '[client-sdk] Missing required preloaded bootstrap config values. Skipping bootstrap.',
                    );
                    return;
                }

                const microservicesClient = createMicroservicesClient({
                    baseUrlUS: runtimeConfig.microservices.baseUrlUS,
                    baseUrlCanada: runtimeConfig.microservices.baseUrlCanada || runtimeConfig.microservices.baseUrlUS,
                    currentCountryCode: runtimeConfig.microservices.currentCountryCode,
                });

                browserWindow.microservicesClient = microservicesClient;
                browserWindow.__clientSdkBootstrapPromise = import('@cecm-web-sdk/core')
                    .then(({ bootstrapSdks }) => bootstrapSdks(runtimeConfig.clientSdk))
                    .then(({ clientSdk }) => {
                        // This bootstrap is shared by the whole page and outlives this effect.
                        // Strict Mode replays effects while the same promise is still pending.
                        browserWindow.clientSdk = clientSdk as IClientSDK;
                        browserWindow.microservicesClient = microservicesClient;

                        return clientSdk as IClientSDK;
                    })
                    .catch((error) => {
                        console.error('[client-sdk] bootstrap failed', error);
                        browserWindow.__clientSdkBootstrapPromise = undefined;
                        return undefined;
                    });

                await browserWindow.__clientSdkBootstrapPromise;
            } catch (error) {
                console.error('[client-sdk] bootstrap failed', error);
            }
        };

        void load();
    }, []);

    return null;
}
